import type { FunctionComponent } from 'react'
import { useState } from 'react'
import { Button, TypographyV2 as Typography } from '@which/seatbelt'
import { ClockIcon } from '@which/seatbelt/src/components/Icons/Miscellaneous/Clock'
import { TickIcon } from '@which/seatbelt/src/components/Icons/Navigational'
import { dynamicGa4DataLayerPush, removeEmptyProps } from '@which/shared'

import { useFullUrl } from '../../../hooks/useFullUrl'
import { getNewsletterSignupApiUrl } from '../../../utils/getNewsletterSignupApiUrl/getNewsletterSignupApiUrl'
import styles from './RecommendationCard.module.scss'

const errorMessage = 'Sign-up error, try again later'

export const RecommendationCard: FunctionComponent<Props> = ({
  form: { email, firstName, lastName, postcode },
  name,
  strapline,
  frequency,
  salesforceName,
}) => {
  const [success, setSuccess] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const buttonTestId = `${name.replace(' ', '-').toLowerCase()}-sign-up-button`

  const { getFullUrl } = useFullUrl()

  const onSignUp = async () => {
    setLoading(true)

    try {
      const finalData = removeEmptyProps({ email, firstName, lastName, postcode })
      const fetchResponse = await fetch(getNewsletterSignupApiUrl(), {
        method: 'post',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...finalData,
          newsletterIds: [
            salesforceName === 'tech_support' ? 'TECH' : salesforceName.toUpperCase(),
          ],
          source: `url(${getFullUrl()})`,
        }),
      })

      if (fetchResponse.ok) {
        const item_text =
          { tech_support: 'tech', food_and_health: 'foodandhealth' }[salesforceName] ??
          salesforceName // this is required because of https://whichonline.atlassian.net/browse/WHC-1055?focusedCommentId=477732 & https://whichonline.atlassian.net/browse/WHC-1069?focusedCommentId=487643
        setLoading(false)
        setSuccess(true)
        dynamicGa4DataLayerPush({
          event: 'goal_fra_signup',
          utagid: 'WHC561DP01',
          item_text,
          action_group: 'widget - recommended newsletters',
          user_email: email,
          user_postcode: postcode,
        })
      } else {
        setLoading(false)
        setErrorMsg(errorMessage)
      }
    } catch (err) {
      setLoading(false)
      setErrorMsg(errorMessage)
    }
  }

  return (
    <div className={styles.recommendationCard}>
      <div>
        <Typography tag="h4" textStyle="sb-text-interface-heading-medium">
          {name}
        </Typography>
        <Typography
          className={styles.strapline}
          tag="p"
          textStyle="sb-text-interface-body-small-regular"
        >
          {strapline}
        </Typography>
      </div>
      <div className={styles.alignBottom}>
        {success ? (
          <span className={styles.successNotification}>
            <TickIcon
              className={styles.tickIcon}
              data-testid="tick-icon"
              width={20}
              height={20}
              viewBox="0 0 16 16"
            />
            <Typography tag="span" textStyle="sb-text-interface-body-default-regular">
              Success
            </Typography>
          </span>
        ) : (
          <div className={styles.cta}>
            <Button
              appearance="secondary"
              onClick={onSignUp}
              enableSpinner={loading}
              data-testid={buttonTestId}
              aria-label={`Sign up for ${name} newsletter`}
            >
              <Typography
                tag="span"
                textStyle="sb-text-body-default-strong"
                className={styles.ctaText}
              >
                Sign up
              </Typography>
            </Button>
            {errorMsg && (
              <Typography
                tag="p"
                textStyle="sb-text-interface-body-small-regular"
                className={styles.errorMessage}
              >
                {errorMsg}
              </Typography>
            )}
          </div>
        )}
        <div className={styles.frequencyNotification}>
          <ClockIcon
            className={styles.clockIcon}
            data-testid="clock-icon"
            width={16}
            height={16}
            viewBox="0 0 11 11"
          />
          <Typography tag="span" textStyle="sb-text-interface-body-small-regular">
            {frequency.toLowerCase() === 'monthly' ? 'Monthly' : 'Weekly'}
          </Typography>
        </div>
      </div>
    </div>
  )
}

type Props = {
  form: {
    email: string
    firstName?: string
    lastName?: string
    postcode?: string
  }
  name: string
  strapline: string
  frequency: string
  salesforceName: string
}
